/**
 * The audio desk's pickers (Dax, 2026-09-29). Each channel lists only its own route of the library, in
 * steps rather than one alphabetical wall: music by genre or mood, then artist, then song; the
 * soundtrack's effects bed by category, then sound; a voiceover by its voice artist, then a whole clip or
 * one of the brand's tagging categories, then the take, each saying what is said. Left on "every", a
 * step's last list is grouped under headings (artist, category, clip) so it never reads as one wall.
 *
 * Every field is one fixed size and reads as its label until something is chosen (Dax, 2026-09-30); the
 * list it opens is as wide as its words need. Choosing the label clears that step; the row's clear
 * empties the channel.
 *
 * Changing an earlier step makes the last one browse (Dax, 2026-10-01): it reads as its label, and
 * opening it lists the new matches first, the loaded row under a Loaded heading beneath them. Nothing
 * loads until a row is picked.
 *
 * A channel holds segments one after another (2026-10-04): the fields show the selected one, and in add
 * mode (the row's ＋) they start empty and read "Add new…", so a pick lays another segment after the last.
 */
import { useState } from 'react';
import { ROUTE_KINDS, VOICE_FIT_LONG_MS, VOICE_FIT_SHORT_MS } from '@studio/contracts';

export interface PickerBed {
  id: string;
  name: string;
  durationMs: number;
  kind: string;
  artist: string | null;
  title: string | null;
  genres: string[];
  moods: string[];
  sfxCategory: string | null;
  description: string | null;
  sourceName: string | null;
  /** The tags of the clip a voiceover was said in, by the brand's category names. */
  clipTags: { category: string; value: string }[];
}

/** The artist choice for rows nobody has credited. */
const UNKNOWN = '\u0000unknown';
/** The longest a take's words run in its option before they are cut short. */
const TAKE_WORDS_MAX = 60;

const secs = (ms: number) => `${Math.max(1, Math.round(ms / 1000))}s`;
const artistOf = (b: PickerBed) => b.artist?.trim() || UNKNOWN;
const artistLabel = (a: string, unknown: string) => (a === UNKNOWN ? unknown : a);
/** Credited artists alphabetically, the uncredited last. */
function artistsIn(rows: PickerBed[]): string[] {
  const all = [...new Set(rows.map(artistOf))];
  return [...all.filter((a) => a !== UNKNOWN).sort((a, b) => a.localeCompare(b)), ...(all.includes(UNKNOWN) ? [UNKNOWN] : [])];
}

/** Rows under headings, the headings in order; rows keep the order they came in. */
function grouped(rows: PickerBed[], heading: (b: PickerBed) => string, order: (a: string, b: string) => number = (a, b) => a.localeCompare(b)): [string, PickerBed[]][] {
  const groups = new Map<string, PickerBed[]>();
  for (const b of rows) groups.set(heading(b), [...(groups.get(heading(b)) ?? []), b]);
  return [...groups.entries()].sort((a, b) => order(a[0], b[0]));
}
const artistOrder = (a: string, b: string) => (a === UNKNOWN ? 1 : b === UNKNOWN ? -1 : a.localeCompare(b));

/**
 * The chosen row always shows. Settled, it is the field's value, and `extra` pins it above the list when
 * the steps would hide it; browsing, `loaded` holds it for the Loaded heading below the new matches.
 */
function withCurrent(current: PickerBed | null, listed: PickerBed[], browsing: boolean): { extra: PickerBed | null; loaded: PickerBed | null; listed: PickerBed[] } {
  if (browsing) return { extra: null, loaded: current, listed: listed.filter((b) => b !== current) };
  return { extra: current && !listed.includes(current) ? current : null, loaded: null, listed };
}

/**
 * Whether an earlier step has changed since the loaded row was chosen. A new loaded row, picked here or
 * set anywhere else (the sparkle, the row's clear), settles it.
 */
function useBrowsing(value: string | null): [boolean, (on: boolean) => void] {
  const [state, setState] = useState({ value, browsing: false });
  if (state.value !== value) setState({ value, browsing: false });
  return [state.value === value && state.browsing, (on) => setState({ value, browsing: on })];
}

export function MusicPicker({ beds, value, adding = false, onPick }: { beds: PickerBed[]; value: string | null; adding?: boolean; onPick: (id: string | null) => void }) {
  const music = beds.filter((b) => ROUTE_KINDS.music.includes(b.kind));
  const current = music.find((b) => b.id === value) ?? null;
  // `g:pop` or `m:happy`: one filter across both lists, since a person asks for either.
  const [filter, setFilter] = useState('');
  const [artist, setArtist] = useState(() => (current ? artistOf(current) : ''));
  const [browsing, setBrowsing] = useBrowsing(value);
  const genres = [...new Set(music.flatMap((b) => b.genres))].sort();
  const moods = [...new Set(music.flatMap((b) => b.moods))].sort();
  const filtered = music.filter((b) => !filter || (filter.startsWith('g:') ? b.genres.includes(filter.slice(2)) : b.moods.includes(filter.slice(2))));
  const artists = artistsIn(filtered);
  const { extra, loaded, listed } = withCurrent(current, filtered.filter((b) => !artist || artistOf(b) === artist), browsing);
  const song = (b: PickerBed) => <option key={b.id} value={b.id}>{b.title || b.name} · {secs(b.durationMs)}</option>;
  /** The loaded row picked again only settles the field: it is already playing, from where it was set. */
  const pick = (id: string | null) => {
    setBrowsing(false);
    if (id !== value) onPick(id);
  };
  return (
    <>
      <label className="field mix-pick">
        <select value={filter} aria-label="Music genre or mood" onChange={(e) => { setFilter(e.target.value); setArtist(''); setBrowsing(true); }}>
          <option value="">{adding ? 'Add new genre…' : 'Genre'}</option>
          {genres.length > 0 && <optgroup label="Genre">{genres.map((g) => <option key={g} value={`g:${g}`}>{g}</option>)}</optgroup>}
          {moods.length > 0 && <optgroup label="Mood">{moods.map((m) => <option key={m} value={`m:${m}`}>{m}</option>)}</optgroup>}
        </select>
      </label>
      <label className="field mix-pick">
        <select value={artist} aria-label="Music artist" onChange={(e) => { setArtist(e.target.value); setBrowsing(true); }}>
          <option value="">{adding ? 'Add new artist…' : 'Artist'}</option>
          {artists.map((a) => <option key={a} value={a}>{artistLabel(a, 'Unknown artist')}</option>)}
        </select>
      </label>
      <label className="field mix-pick">
        <select value={browsing ? '' : value ?? ''} aria-label="Music" onChange={(e) => pick(e.target.value || null)}>
          <option value="">{adding ? 'Add new song…' : 'Song'}</option>
          {extra && song(extra)}
          {artist
            ? listed.map(song)
            : grouped(listed, artistOf, artistOrder).map(([a, rows]) => <optgroup key={a} label={artistLabel(a, 'Unknown artist')}>{rows.map(song)}</optgroup>)}
          {loaded && <optgroup label="Loaded">{song(loaded)}</optgroup>}
        </select>
      </label>
    </>
  );
}

export function SoundtrackPicker({ beds, value, adding = false, onPick }: { beds: PickerBed[]; value: string | null; adding?: boolean; onPick: (id: string | null) => void }) {
  const effects = beds.filter((b) => ROUTE_KINDS.sfx.includes(b.kind));
  const current = effects.find((b) => b.id === value) ?? null;
  const [category, setCategory] = useState(() => current?.sfxCategory ?? '');
  const [browsing, setBrowsing] = useBrowsing(value);
  const categories = [...new Set(effects.map((b) => b.sfxCategory).filter((c): c is string => Boolean(c)))].sort();
  const { extra, loaded, listed } = withCurrent(current, effects.filter((b) => !category || b.sfxCategory === category), browsing);
  const sound = (b: PickerBed) => <option key={b.id} value={b.id}>{b.name} · {secs(b.durationMs)}</option>;
  /** The loaded row picked again only settles the field: it is already playing, from where it was set. */
  const pick = (id: string | null) => {
    setBrowsing(false);
    if (id !== value) onPick(id);
  };
  return (
    <>
      <label className="field mix-pick">
        <select value={category} aria-label="Soundtrack category" onChange={(e) => { setCategory(e.target.value); setBrowsing(true); }}>
          <option value="">{adding ? 'Add new category…' : 'Category'}</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <label className="field mix-pick">
        <select value={browsing ? '' : value ?? ''} aria-label="Soundtrack" onChange={(e) => pick(e.target.value || null)}>
          <option value="">{adding ? 'Add new sound…' : 'Sound'}</option>
          {extra && sound(extra)}
          {category
            ? listed.map(sound)
            : grouped(listed, (b) => b.sfxCategory ?? 'Uncategorised', (a, b) => (a === 'Uncategorised' ? 1 : b === 'Uncategorised' ? -1 : a.localeCompare(b)))
              .map(([c, rows]) => <optgroup key={c} label={c}>{rows.map(sound)}</optgroup>)}
          {loaded && <optgroup label="Loaded">{sound(loaded)}</optgroup>}
        </select>
      </label>
    </>
  );
}

/**
 * `onScreen` is the creator most of the cut shows: their voice is offered first, so the voice matches
 * the face, and any other is one change away (Dax, 2026-09-29).
 */
/** The Voiceover's middle step: a whole clip, or one value of one of the brand's tagging categories. */
const CLIP = 'clip:';
const TAG = 'tag:';
/** A Take value naming a whole clip of the composition's own, rather than a library take. */
export const WHOLE_CLIP = 'whole:';
const SEP = '\u0001';

/**
 * Takes in the order they are useful to a cut `cutMs` long (Dax, 2026-09-29): first those that fit it (a
 * breath short to a few seconds long, closest first), then longer ones to pick a passage from (shortest
 * first), and last the ones too short to cover it (longest first).
 */
export function takesByFit(takes: PickerBed[], cutMs: number): [string, PickerBed[]][] {
  const fits = takes.filter((b) => b.durationMs >= cutMs - VOICE_FIT_SHORT_MS && b.durationMs <= cutMs + VOICE_FIT_LONG_MS)
    .sort((a, b) => Math.abs(a.durationMs - cutMs) - Math.abs(b.durationMs - cutMs));
  const longer = takes.filter((b) => b.durationMs > cutMs + VOICE_FIT_LONG_MS).sort((a, b) => a.durationMs - b.durationMs);
  const shorter = takes.filter((b) => b.durationMs < cutMs - VOICE_FIT_SHORT_MS).sort((a, b) => b.durationMs - a.durationMs);
  const groups: [string, PickerBed[]][] = [
    [`Fits the ${secs(cutMs)} cut`, fits],
    ['Longer: drag to choose a passage', longer],
    ['Shorter than the cut', shorter],
  ];
  return groups.filter(([, rows]) => rows.length > 0);
}

/** One of the composition's own clips, whose whole audio can be the voiceover. */
export interface PickerClip { assetId: string; name: string; durationMs: number; /** Who the clip shows: the voice the artist step narrows by. */ person: string | null }

export function VoicePicker({ beds, value, clipLine, clips: ownClips, onScreen, cutMs, adding = false, onPick }: {
  beds: PickerBed[];
  /** A library take's id, or WHOLE_CLIP and a clip's asset id. */
  value: string | null;
  /** A line picked straight off a clip in the Analyser, shown as the current take. */
  clipLine: string | null;
  onScreen: string | null;
  /** The composition's own clips, offered whole under the Take menu (2026-10-04). */
  clips: PickerClip[];
  /** The cut's length, which the takes are ordered against. */
  cutMs: number;
  adding?: boolean;
  onPick: (pick: { bedId: string } | { assetId: string } | null) => void;
}) {
  const voices = beds.filter((b) => ROUTE_KINDS.voice.includes(b.kind));
  const current = voices.find((b) => b.id === value) ?? null;
  const artists = artistsIn(voices);
  const [artist, setArtist] = useState(() => (current ? artistOf(current) : onScreen && artists.includes(onScreen) ? onScreen : ''));
  const [narrow, setNarrow] = useState('');
  const [browsing, setBrowsing] = useBrowsing(value);
  const byArtist = voices.filter((b) => !artist || artistOf(b) === artist);
  // What the middle step offers is whatever this artist's takes were said in, and how those clips are tagged.
  const clips = [...new Set(byArtist.map((b) => b.sourceName).filter((n): n is string => Boolean(n)))].sort((a, b) => a.localeCompare(b));
  const categories = new Map<string, Set<string>>();
  for (const b of byArtist) for (const t of b.clipTags) categories.set(t.category, (categories.get(t.category) ?? new Set()).add(t.value));
  const matches = (b: PickerBed) => {
    if (!narrow) return true;
    if (narrow.startsWith(CLIP)) return b.sourceName === narrow.slice(CLIP.length);
    const [category, v] = narrow.slice(TAG.length).split(SEP);
    return b.clipTags.some((t) => t.category === category && t.value === v);
  };
  const { extra, loaded, listed } = withCurrent(current, byArtist.filter(matches), browsing);
  const words = (b: PickerBed) => {
    const said = (b.description ?? b.name).trim();
    return said.length > TAKE_WORDS_MAX ? `${said.slice(0, TAKE_WORDS_MAX - 1)}…` : said;
  };
  const take = (b: PickerBed, clip: boolean) => (
    <option key={b.id} value={b.id}>“{words(b)}”{clip && b.sourceName ? ` · ${b.sourceName}` : ''} · {secs(b.durationMs)}</option>
  );
  const oneClip = narrow.startsWith(CLIP);
  // The composition's own clips follow the same steps: the voice chosen, and the clip chosen (a tagging
  // category says nothing about them, so it leaves none).
  const wholeClips = ownClips.filter((c) => (!artist || (c.person?.trim() || UNKNOWN) === artist)
    && (!narrow || (narrow.startsWith(CLIP) && c.name === narrow.slice(CLIP.length))));
  return (
    <>
      <label className="field mix-pick">
        <select value={artist} aria-label="Voice artist" onChange={(e) => { setArtist(e.target.value); setNarrow(''); setBrowsing(true); }}>
          <option value="">{adding ? 'Add new voice…' : 'Voice'}</option>
          {artists.map((a) => <option key={a} value={a}>{artistLabel(a, 'Uncredited')}</option>)}
        </select>
      </label>
      <label className="field mix-pick">
        <select value={narrow} aria-label="Voiceover clip or category" onChange={(e) => { setNarrow(e.target.value); setBrowsing(true); }}>
          <option value="">{adding ? 'Add new category…' : 'Category'}</option>
          {clips.length > 0 && <optgroup label="Clip">{clips.map((c) => <option key={c} value={`${CLIP}${c}`}>{c}</option>)}</optgroup>}
          {[...categories.entries()].map(([category, values]) => (
            <optgroup key={category} label={category}>
              {[...values].sort((a, b) => a.localeCompare(b)).map((v) => <option key={v} value={`${TAG}${category}${SEP}${v}`}>{v}</option>)}
            </optgroup>
          ))}
        </select>
      </label>
      <label className="field mix-pick">
        <select value={browsing ? '' : value ?? (clipLine ? 'clip' : '')} aria-label="Voiceover"
          onChange={(e) => {
            // The line off a clip and the loaded take are already playing: picking either only settles the field.
            setBrowsing(false);
            const v = e.target.value;
            if (v === 'clip' || v === (value ?? '')) return;
            if (v.startsWith(WHOLE_CLIP)) onPick({ assetId: v.slice(WHOLE_CLIP.length) });
            else onPick(voices.some((b) => b.id === v) ? { bedId: v } : null);
          }}>
          <option value="">{adding ? 'Add new take…' : 'Take'}</option>
          {clipLine && !browsing && <option value="clip">{clipLine}</option>}
          {extra && take(extra, true)}
          {/* Headed by how well each take fits the cut; each says its clip unless one clip is chosen. */}
          {takesByFit(listed, cutMs).map(([heading, rows]) => <optgroup key={heading} label={heading}>{rows.map((b) => take(b, !oneClip))}</optgroup>)}
          {wholeClips.length > 0 && (
            <optgroup label="Whole clip">
              {wholeClips.map((c) => <option key={c.assetId} value={`${WHOLE_CLIP}${c.assetId}`}>Whole clip · {c.name} · {secs(c.durationMs)}</option>)}
            </optgroup>
          )}
          {browsing && (loaded || clipLine) && (
            <optgroup label="Loaded">{loaded ? take(loaded, true) : <option value="clip">{clipLine}</option>}</optgroup>
          )}
        </select>
      </label>
    </>
  );
}
